import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  editTestTranslation,
  getAudioSignedUrl,
  getImageSignedUrl,
  getPdfSignedUrl,
  postTestTranslation,
  translateTestgroupLanguage,
  uploadFileToS3,
} from '../API/services/screeningService';
import { useLanguageTranslations } from './useLanguageTranslations';
import { CREATE_ABM_TESTGROUP_SCREENING } from '../constants/routes';
import { TestFileType } from '../constants/enums';
import { removeQueryParams } from '../utils/removeQueryParams';
import {
  setTestgroupToTranslate,
  setTranslatedTests,
} from '../redux/actions/testGroupCreatorActions';

export const useTranslateTestgroup = () => {
  const {
    testToTranslate,
    testgroupTranslated,
    originalLanguage,
    originalIsoCode,
    isoCodeToTranslate,
    languageToTranslate,
    translatedTests,
  } = useSelector((state) => state.testGroups);

  const navigate = useHistory();
  const { testGroupLanguageList } = useLanguageTranslations();
  const dispatch = useDispatch();

  const updateTranslatedTests = (test) => {
    const updatedTests = {
      ...translatedTests,
      [test.id]: test,
    };
    dispatch(setTranslatedTests(updatedTests));
  };

  const setTranslatedTestgroup = (testgroup) => {
    dispatch(setTestgroupToTranslate(testgroup));
  };

  const handleUpdateTranslatedTestgroup = (key, value) => {
    const copyTG = {
      ...testgroupTranslated,
      [key]: value,
    };
    setTranslatedTestgroup(copyTG);
  };

  const disableTestgroupTranslation =
    !testgroupTranslated?.objetives?.[languageToTranslate] ||
    !testgroupTranslated?.detail?.[languageToTranslate] ||
    !testgroupTranslated?.summary?.[languageToTranslate] ||
    !testgroupTranslated?.nameTranslated?.[languageToTranslate];

  const uploadAndUpdateFile = async (file) => {
    if (!TestFileType[file.fileType]) return;
    let signedUrl;
    if (file.fileType === TestFileType.IMAGE)
      signedUrl = await getImageSignedUrl(file.fileObject.type);
    if (file.fileType === TestFileType.AUDIO)
      signedUrl = await getAudioSignedUrl(file.fileObject.type);
    if (file.fileType === TestFileType.PDF) signedUrl = await getPdfSignedUrl(file.fileObject.type);

    await uploadFileToS3(signedUrl.fileUrl, file.fileObject, file.fileObject.type);

    file.displayName = file.name;
    file.name = signedUrl.fileId;
    file.extension = signedUrl.fileExtension;
    file.url = removeQueryParams(signedUrl.fileUrl);
    delete file.fileObject;
  };

  const uploadAndUpdateFiles = async (test) => {
    const testsPromises =
      test.testFiles?.filter((file) => file.fileObject).map((file) => uploadAndUpdateFile(file)) ??
      [];

    const optionsPromises =
      test.options
        ?.filter((option) => option.files?.length)
        .flatMap((option) => option.files)
        .filter((file) => file.fileObject)
        .map((file) => uploadAndUpdateFile(file)) ?? [];

    const promises = [...testsPromises, ...optionsPromises];

    await Promise.all(promises);
  };

  const saveTestTranslation = async (testToTranslate) => {
    await uploadAndUpdateFiles(testToTranslate);
    const data = {
      testId: testToTranslate.id,
      language: languageToTranslate,
      ...testToTranslate,
    };
    if (translatedTests[testToTranslate.id]) {
      await editTestTranslation(data);
    } else {
      await postTestTranslation(data);
    }
  };

  const setAlreadyTranslatedTests = (tests) => {
    const testsHash = {};
    if (tests) {
      tests.forEach((test) => {
        if (test && test?.testTranslations) {
          const inLanguage = test.testTranslations.find((t) => t.language === languageToTranslate);
          if (inLanguage) {
            const actualId = test.id ?? test?.testId;
            testsHash[actualId] = {
              ...inLanguage,
              id: actualId,
              difficultyId: test.difficultyId,
              theme: test.theme,
            };
          }
        }
      });
      dispatch(setTranslatedTests(testsHash));
    }
  };

  const saveTestgroupTranslated = async (id, step) => {
    const dataToPatch = {
      ...testgroupTranslated,
      id,
      languageToTranslate,
      ...(step.subtitle?.[languageToTranslate] &&
      step.subtitle?.[languageToTranslate]?.trim() !== ''
        ? { customStep: step }
        : {}),
    };
    try {
      await translateTestgroupLanguage(dataToPatch);
      navigate.push(CREATE_ABM_TESTGROUP_SCREENING);
    } catch (e) {
      throw new Error(e?.message);
    }
  };

  return {
    testgroupTranslated,
    translatedTests,
    testGroupLanguageList,
    originalLanguage,
    originalIsoCode,
    languageToTranslate,
    isoCodeToTranslate,
    disableTestgroupTranslation,
    testToTranslate,
    setTranslatedTestgroup,
    setAlreadyTranslatedTests,
    handleUpdateTranslatedTestgroup,
    updateTranslatedTests,
    saveTestTranslation,
    saveTestgroupTranslated,
  };
};
